import React, { useEffect } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Divider,
} from "@mui/material";
import { useAdmin } from "../../context/AdminContext";
import { formatCurrency } from "../../utils/utils";

// Simple stat card: title + single value
const StatCard = ({
  title,
  value,
  tooltip,
}: {
  title: string;
  value: string | number;
  tooltip?: string;
}) => {
  const content = (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" sx={{ color: "#666" }} gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="div" sx={{ color: "#222" }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

// Stat card with this month / last month breakdown
const PeriodStatCard = ({
  title,
  thisMonth,
  lastMonth,
  tooltip,
  format,
}: {
  title: string;
  thisMonth: number;
  lastMonth: number;
  tooltip?: string;
  format?: (v: number) => string;
}) => {
  const fmt = format || ((v: number) => String(v));
  const diff = thisMonth - lastMonth;
  const diffPct = lastMonth > 0 ? ((diff / lastMonth) * 100).toFixed(0) : null;
  const diffColor = diff > 0 ? "#4caf50" : diff < 0 ? "#f44336" : "#999";

  const content = (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" sx={{ color: "#666" }} gutterBottom>
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 3, alignItems: "baseline", mt: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: "#666" }}>
              This Month
            </Typography>
            <Typography variant="h5" component="div" sx={{ color: "#222" }}>
              {fmt(thisMonth)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "#666" }}>
              Last Month
            </Typography>
            <Typography variant="h5" component="div" sx={{ color: "#666" }}>
              {fmt(lastMonth)}
            </Typography>
          </Box>
        </Box>
        {diffPct !== null && (
          <Typography variant="caption" sx={{ color: diffColor, mt: 0.5, display: "block" }}>
            {diff > 0 ? "+" : ""}
            {diffPct}% vs last month
          </Typography>
        )}
      </CardContent>
    </Card>
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export const AdminDashboard: React.FC = () => {
  const { stats, loading, fetchStats } = useAdmin();

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading || !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: "#222" }}>
        Admin Dashboard
      </Typography>

      {/* General Stats */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "#555" }}>
        General
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers || 0}
            tooltip="Total number of registered users"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Premium Users"
            value={stats.premiumUsers || 0}
            tooltip="Users who own at least one workspace with a plan"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Premium Workspaces"
            value={stats.plans?.total || 0}
            tooltip="Total workspaces with an active plan"
          />
        </Grid>
      </Grid>

      {/* Plan Breakdown */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1, color: "#555" }}>
        Plan Breakdown
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} md={3}>
          <StatCard
            title="Starter Plans"
            value={stats.plans?.starter || 0}
            tooltip="Workspaces on Starter plan"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <StatCard
            title="Pro Plans"
            value={stats.plans?.pro || 0}
            tooltip="Workspaces on Pro plan"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <StatCard
            title="Custom Plans"
            value={stats.plans?.custom || 0}
            tooltip="Workspaces with custom pricing plans"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Video Generation Stats */}
      <Typography variant="h6" sx={{ mb: 1, color: "#555" }}>
        Prompt to Video (Kie API)
      </Typography>

      {/* Sora2 sub-group */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#333", fontWeight: 600 }}>
            Sora2
          </Typography>
          <Typography variant="caption" sx={{ color: "#888" }}>
            Total: {stats.sora2Videos?.thisMonth || 0} this month / {stats.sora2Videos?.lastMonth || 0} last month
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <PeriodStatCard
              title="Standard"
              thisMonth={stats.sora2Videos?.byVersion?.standard?.thisMonth || 0}
              lastMonth={stats.sora2Videos?.byVersion?.standard?.lastMonth || 0}
              tooltip="Successful Sora2 standard generations (text-to-video + image-to-video)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <PeriodStatCard
              title="Pro"
              thisMonth={stats.sora2Videos?.byVersion?.pro?.thisMonth || 0}
              lastMonth={stats.sora2Videos?.byVersion?.pro?.lastMonth || 0}
              tooltip="Successful Sora2 Pro generations (pro-text-to-video + pro-image-to-video)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <PeriodStatCard
              title="Storyboard"
              thisMonth={stats.sora2Videos?.byVersion?.storyboard?.thisMonth || 0}
              lastMonth={stats.sora2Videos?.byVersion?.storyboard?.lastMonth || 0}
              tooltip="Successful Sora2 storyboard generations"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Seedance sub-group */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#333", fontWeight: 600 }}>
            Seedance
          </Typography>
          <Typography variant="caption" sx={{ color: "#888" }}>
            Total: {stats.seedanceVideos?.thisMonth || 0} this month / {stats.seedanceVideos?.lastMonth || 0} last month
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <PeriodStatCard
              title="1.5 Pro"
              thisMonth={stats.seedanceVideos?.byVersion?.v15Pro?.thisMonth || 0}
              lastMonth={stats.seedanceVideos?.byVersion?.v15Pro?.lastMonth || 0}
              tooltip="Successful Seedance 1.5 Pro generations (bytedance/seedance-1-5-pro)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <PeriodStatCard
              title="2.0"
              thisMonth={stats.seedanceVideos?.byVersion?.v2?.thisMonth || 0}
              lastMonth={stats.seedanceVideos?.byVersion?.v2?.lastMonth || 0}
              tooltip="Successful Seedance 2.0 generations (bytedance/seedance-2)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <PeriodStatCard
              title="2.0 Fast"
              thisMonth={stats.seedanceVideos?.byVersion?.v2Fast?.thisMonth || 0}
              lastMonth={stats.seedanceVideos?.byVersion?.v2Fast?.lastMonth || 0}
              tooltip="Successful Seedance 2.0 Fast generations (bytedance/seedance-2-fast)"
            />
          </Grid>
          {((stats.seedanceVideos?.byVersion?.v1?.thisMonth || 0) > 0 ||
            (stats.seedanceVideos?.byVersion?.v1?.lastMonth || 0) > 0) && (
            <Grid item xs={12} sm={6} md={3}>
              <PeriodStatCard
                title="V1 (legacy)"
                thisMonth={stats.seedanceVideos?.byVersion?.v1?.thisMonth || 0}
                lastMonth={stats.seedanceVideos?.byVersion?.v1?.lastMonth || 0}
                tooltip="Successful legacy Seedance V1 generations (text-to-video + image-to-video)"
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Veo3 sub-group */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#333", fontWeight: 600 }}>
            Veo3
          </Typography>
          <Typography variant="caption" sx={{ color: "#888" }}>
            Total: {stats.veo3Videos?.thisMonth || 0} this month / {stats.veo3Videos?.lastMonth || 0} last month
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <PeriodStatCard
              title="Fast"
              thisMonth={stats.veo3Videos?.byVersion?.fast?.thisMonth || 0}
              lastMonth={stats.veo3Videos?.byVersion?.fast?.lastMonth || 0}
              tooltip="Successful Veo3 Fast generations (veo3_fast)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <PeriodStatCard
              title="Quality"
              thisMonth={stats.veo3Videos?.byVersion?.quality?.thisMonth || 0}
              lastMonth={stats.veo3Videos?.byVersion?.quality?.lastMonth || 0}
              tooltip="Successful Veo3 Quality generations (veo3)"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Kie API Cost */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1, color: "#555" }}>
        Kie API Cost
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <PeriodStatCard
            title="Credits Spent"
            thisMonth={stats.kieApiCost?.thisMonth?.credits || 0}
            lastMonth={stats.kieApiCost?.lastMonth?.credits || 0}
            tooltip="Total Kie credits spent (Sora2 + Seedance + Veo3)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PeriodStatCard
            title="Estimated Cost (USD)"
            thisMonth={stats.kieApiCost?.thisMonth?.costUSD || 0}
            lastMonth={stats.kieApiCost?.lastMonth?.costUSD || 0}
            tooltip="Credits x $0.005"
            format={(v) => formatCurrency(v)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* HeyGen Stats */}
      <Typography variant="h6" sx={{ mb: 1, color: "#555" }}>
        HeyGen
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <PeriodStatCard
            title="HeyGen Videos"
            thisMonth={stats.heygenVideos?.thisMonth || 0}
            lastMonth={stats.heygenVideos?.lastMonth || 0}
            tooltip="HeyGen template videos generated"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PeriodStatCard
            title="IV Avatar Videos"
            thisMonth={stats.ivAvatarVideos?.thisMonth || 0}
            lastMonth={stats.ivAvatarVideos?.lastMonth || 0}
            tooltip="IV Avatar videos generated"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PeriodStatCard
            title="Video Agent"
            thisMonth={stats.videoAgentVideos?.thisMonth || 0}
            lastMonth={stats.videoAgentVideos?.lastMonth || 0}
            tooltip="Videos generated via HeyGen Video Agent"
          />
        </Grid>
      </Grid>

      {/* Subscription Revenue */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1, color: "#555" }}>
        Subscription Revenue
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <PeriodStatCard
            title="Monthly Plan Revenue"
            thisMonth={stats.subscriptionRevenue?.thisMonth?.monthly || 0}
            lastMonth={stats.subscriptionRevenue?.lastMonth?.monthly || 0}
            tooltip="Sum of plan monthly amounts (pricePerUGC x videoLimit)"
            format={(v) => formatCurrency(v)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PeriodStatCard
            title="Usage-Based Revenue"
            thisMonth={stats.subscriptionRevenue?.thisMonth?.usage || 0}
            lastMonth={stats.subscriptionRevenue?.lastMonth?.usage || 0}
            tooltip="Revenue based on actual credits used"
            format={(v) => formatCurrency(v)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
